import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

interface SampleReportAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

interface LabelValueRow {
  label: string;
  value: string;
}

interface SampleFinding {
  title: string;
  severity: string;
  affectedArea: string;
  wcagReference: string;
  whyItMatters: string;
  suggestedRemediation: string;
  reviewStatus: string;
}

export default function SampleReportPage() {
  const { t } = useTranslation('marketing');
  const overviewItems = t('pages.sampleReport.overview.items', { returnObjects: true }) as string[];
  const executiveSummaryRows = t('pages.sampleReport.executiveSummary.rows', { returnObjects: true }) as LabelValueRow[];
  const findings = t('pages.sampleReport.findings.items', { returnObjects: true }) as SampleFinding[];
  const workflowPoints = t('pages.sampleReport.remediation.points', { returnObjects: true }) as string[];
  const humanReviewPoints = t('pages.sampleReport.aiHumanReview.points', { returnObjects: true }) as string[];
  const sidebarPoints = t('pages.sampleReport.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.sampleReport.cta.actions', { returnObjects: true }) as SampleReportAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.sampleReport.meta.title')}
      metaDescription={t('pages.sampleReport.meta.description')}
      canonicalPath="/sample-report"
      eyebrow={t('pages.sampleReport.hero.eyebrow')}
      title={t('pages.sampleReport.hero.title')}
      intro={t('pages.sampleReport.hero.intro')}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.sampleReport.sidebar.title')}</h2>
            <p>{t('pages.sampleReport.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.sampleReport.disclaimer.title')}</h2>
            <p>{t('pages.sampleReport.disclaimer.body')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.sampleReport.intro.title')}</h2>
        <p>{t('pages.sampleReport.intro.body')}</p>
        <p>{t('pages.sampleReport.intro.note')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.sampleReport.overview.title')}</h2>
        <ul className="acc-public-page-list">
          {overviewItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.sampleReport.executiveSummary.title')}</h2>
        <div className="acc-public-page-meta">
          {executiveSummaryRows.map((row) => (
            <div className="acc-public-page-meta-row" key={row.label}>
              <span className="acc-public-page-meta-label">{row.label}</span>
              <span className="acc-public-page-meta-value">{row.value}</span>
            </div>
          ))}
        </div>
        <p>
          <strong>{t('pages.sampleReport.executiveSummary.summaryLabel')}</strong>{' '}
          {t('pages.sampleReport.executiveSummary.summary')}
        </p>
      </section>

      <section className="acc-public-page-section" aria-labelledby="sample-findings-title">
        <div>
          <h2 id="sample-findings-title">{t('pages.sampleReport.findings.title')}</h2>
          <p>{t('pages.sampleReport.findings.intro')}</p>
        </div>

        <div className="acc-public-page-grid">
          {findings.map((finding) => (
            <article key={finding.title} className="acc-public-page-subcard">
              <h3>{finding.title}</h3>
              <ul className="acc-public-page-list">
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.severity')}</strong>
                  <span>{finding.severity}</span>
                </li>
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.affectedArea')}</strong>
                  <span>{finding.affectedArea}</span>
                </li>
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.wcagReference')}</strong>
                  <span>{finding.wcagReference}</span>
                </li>
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.whyItMatters')}</strong>
                  <span>{finding.whyItMatters}</span>
                </li>
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.suggestedRemediation')}</strong>
                  <span>{finding.suggestedRemediation}</span>
                </li>
                <li>
                  <strong>{t('pages.sampleReport.findings.labels.reviewStatus')}</strong>
                  <span>{finding.reviewStatus}</span>
                </li>
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.sampleReport.remediation.title')}</h2>
        <p>{t('pages.sampleReport.remediation.body')}</p>
        <ul className="acc-public-page-list">
          {workflowPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.sampleReport.aiHumanReview.title')}</h2>
        <p>{t('pages.sampleReport.aiHumanReview.body')}</p>
        <p>{t('pages.sampleReport.aiHumanReview.pointsIntro')}</p>
        <ul className="acc-public-page-list">
          {humanReviewPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="sample-report-cta-title">
        <div>
          <h2 id="sample-report-cta-title">{t('pages.sampleReport.cta.title')}</h2>
          <p>{t('pages.sampleReport.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
